import React, { useEffect, useState } from "react";
import { Coffee, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { Card, Button, PageHeader, PageLoader } from "../lib/ui";
import { useToast } from "../lib/toast";
import { useT } from "../lib/i18n";
import { compressImage } from "../lib/image";
import { PhotoEvidence } from "../components/PhotoEvidence";

// Coffee Machine Counter — operational AUDIT log. Never touches inventory.
// Configurable counter labels (per branch). Mandatory photo of the machine display.
export default function CoffeeMachineCounter() {
  const { t } = useT();
  const toast = useToast();
  const [config, setConfig] = useState(null);
  const [values, setValues] = useState({});
  const [photos, setPhotos] = useState([]);
  const [notes, setNotes] = useState("");
  const [logs, setLogs] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadLogs = () => api.get("/beverage/machine-counter").then(({ data }) => setLogs(data)).catch(() => setLogs([]));

  useEffect(() => {
    api.get("/beverage/machine-counter/config")
      .then(({ data }) => setConfig(data))
      .catch(() => setConfig({ enabled: false, counter_labels: [] }));
    loadLogs();
  }, []);

  const submit = async () => {
    if (photos.length === 0) { toast.error(t("Photo of the machine display is required (min 1)")); return; }
    const counters = (config?.counter_labels || [])
      .map((label) => ({ label, value: Number(values[label] ?? 0) }));
    setSaving(true);
    try {
      await api.post("/beverage/machine-counter", { counters, photos, notes });
      toast.success(t("Machine counter log saved"));
      setValues({}); setPhotos([]); setNotes("");
      loadLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Failed to save"));
    } finally { setSaving(false); }
  };

  if (!config) return <PageLoader />;

  return (
    <div className="animate-fade-up pb-24 lg:pb-2" data-testid="coffee-machine-counter">
      <PageHeader icon={Coffee} title={t("Coffee Machine Counter")}
        subtitle={t("Nightly espresso machine usage audit — not inventory")} />

      {!config.enabled ? (
        <Card className="mt-4 flex items-center gap-3 border-amber-200 bg-amber-50 p-5 text-amber-800" data-testid="counter-disabled">
          <AlertTriangle size={20} />
          <p className="text-sm font-semibold">
            {t("Coffee Machine Counter is not enabled for this branch.")}
          </p>
        </Card>
      ) : (
        <Card className="mt-4 space-y-4 p-5" data-testid="counter-form">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {(config.counter_labels || []).map((label) => (
              <div key={label}>
                <label className="mb-1 block text-xs font-semibold text-ink-soft">{label}</label>
                <input type="number" min="0" value={values[label] ?? ""}
                  onChange={(e) => setValues({ ...values, [label]: e.target.value })}
                  data-testid={`counter-input-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="w-full rounded-xl border border-line bg-app px-3 py-2 text-sm" placeholder="0" />
              </div>
            ))}
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-ink-soft">{t("Notes")} ({t("optional")})</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="counter-notes"
              className="w-full rounded-xl border border-line bg-app px-3 py-2 text-sm" />
          </div>

          <PhotoEvidence
            tokens={photos} setTokens={setPhotos}
            uploadFile={(file) => compressImage(file, 1024, 0.7)}
            max={4} testid="counter-photos"
            label={t("Machine display photo (required)")}
            hint={t("Camera or gallery — at least 1 photo of the counter display")} />

          <Button onClick={submit} loading={saving} disabled={photos.length === 0}
            data-testid="counter-submit" className="w-full sm:w-auto">
            {t("Save Daily Log")}
          </Button>
        </Card>
      )}

      <h3 className="mt-8 mb-2 text-sm font-bold text-ink">{t("Recent Logs")}</h3>
      <div className="space-y-2" data-testid="counter-logs">
        {logs.length === 0 && <p className="text-sm text-ink-mute">{t("No machine logs yet.")}</p>}
        {logs.map((l) => (
          <Card key={l.id} className="p-4" data-testid={`counter-log-${l.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-ink">{l.log_no}</span>
              <span className="text-[11px] text-ink-mute">{(l.created_at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {(l.counters || []).map((c, i) => (
                <span key={i} className="rounded-lg bg-app px-2 py-1 text-[11px] text-ink-soft">
                  {c.label}: <b>{c.value}</b>
                </span>
              ))}
            </div>
            {l.photos?.length > 0 && (
              <div className="mt-2 flex gap-2">
                {l.photos.map((p, i) => <img key={i} src={p} alt="" className="h-14 w-14 rounded-lg border border-line object-cover" />)}
              </div>
            )}
            {l.notes && <p className="mt-2 text-xs text-ink-mute">{l.notes}</p>}
          </Card>
        ))}
      </div>
    </div>
  );
}
